import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  componentName?: string;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary Component
 * Catches JavaScript errors anywhere in the child component tree and displays a fallback UI.
 * Logs errors to console and optionally to the backend for Railway monitoring.
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { componentName, onError } = this.props;
    
    // Log error to console with component context
    console.error(`[ErrorBoundary] ${componentName || 'Unknown'} crashed:`, {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
    });

    // Store error info for display
    this.setState({ errorInfo });

    // Call custom error handler if provided
    if (onError) {
      onError(error, errorInfo);
    }

    // Log to backend for Railway monitoring
    this.logErrorToBackend(error, errorInfo, componentName);
  }

  private async logErrorToBackend(error: Error, errorInfo: ErrorInfo, componentName?: string) {
    try {
      const API_URL = import.meta.env.VITE_API_URL || '/api';
      const tgWebApp = window.Telegram?.WebApp;
      
      await fetch(`${API_URL}/logs/frontend-error`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          error: {
            message: error.message,
            name: error.name,
            stack: error.stack,
          },
          componentStack: errorInfo.componentStack,
          componentName: componentName || 'Unknown',
          userAgent: navigator.userAgent,
          url: window.location.href,
          timestamp: new Date().toISOString(),
          telegramData: {
            initData: tgWebApp?.initData ? 'present' : 'missing',
            colorScheme: tgWebApp?.colorScheme,
          },
        }),
      });
    } catch (logError) {
      // Silent fail - don't break the app if logging fails
      console.error('[ErrorBoundary] Failed to log error to backend:', logError);
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  public render() {
    const { hasError, error } = this.state;
    const { children, fallback, componentName } = this.props;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      // Default error UI
      return (
        <div className="card p-6 bg-red-500/10 border border-red-500/30">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-red-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-red-400 mb-2">
                {componentName ? `${componentName} Error` : 'Something went wrong'}
              </h3>
              <p className="text-sm text-white/60 mb-4">
                {error?.message || 'An unexpected error occurred. Please try again.'}
              </p>
              <button
                onClick={this.handleRetry}
                className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 text-sm transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>
            </div>
          </div>
          
          {/* Debug info in development */}
          {import.meta.env.DEV && (
            <details className="mt-4 text-xs text-white/40">
              <summary className="cursor-pointer hover:text-white/60">Error Details</summary>
              <pre className="mt-2 p-2 bg-black/30 rounded overflow-auto max-h-40">
                {error?.stack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
